import { useEffect, useMemo, useState } from "react";
import {
  and,
  collection,
  doc,
  limit,
  onSnapshot,
  or,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { firebase } from "../services/firebase-config";
import { COLLECTIONS } from "../services/config";

const EMPTY_PROGRESS = {
  profileComplete: false,
  clientAdded: false,
  buildingAdded: false,
  crewAdded: false,
  shiftScheduled: false,
};

const OPTIONAL_PROFILE_FIELD_LABELS = {
  companyAddress: "company address",
  companyZip: "ZIP code",
};

const isFilled = (value) => String(value || "").trim().length > 0;

const hasEmployeeRole = (role) => {
  if (Array.isArray(role)) {
    return role.includes("Employee");
  }

  return role === "Employee";
};

const getCompanyProfileCompletion = (userData) => {
  const safeUserData = userData || {};

  return [
    safeUserData.companyName,
    // safeUserData.companyAddress,
    safeUserData.companyCity,
    safeUserData.companyState,
    // safeUserData.companyZip,
    safeUserData.phoneNumber || safeUserData.phone,
  ].every(isFilled);
};

const getOptionalProfileFieldsMissing = (userData) => {
  const safeUserData = userData || {};

  return {
    companyAddress: !isFilled(safeUserData.companyAddress),
    companyZip: !isFilled(safeUserData.companyZip),
  };
};

const areProgressStatesEqual = (left = EMPTY_PROGRESS, right = EMPTY_PROGRESS) =>
  left.profileComplete === right.profileComplete &&
  left.clientAdded === right.clientAdded &&
  left.buildingAdded === right.buildingAdded &&
  left.crewAdded === right.crewAdded &&
  left.shiftScheduled === right.shiftScheduled;

export default function useOnboardingProgress(userId) {
  const [userData, setUserData] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [clientAdded, setClientAdded] = useState(false);
  const [crewAdded, setCrewAdded] = useState(false);
  const [shiftScheduled, setShiftScheduled] = useState(false);
  const [loadingUser, setLoadingUser] = useState(Boolean(userId));
  const [loadingCollections, setLoadingCollections] = useState(Boolean(userId));

  useEffect(() => {
    if (!userId) {
      setUserData(null);
      setCompanyId(null);
      setClientAdded(false);
      setCrewAdded(false);
      setShiftScheduled(false);
      setLoadingUser(false);
      setLoadingCollections(false);
      return undefined;
    }

    setLoadingUser(true);

    const unsubscribe = onSnapshot(
      doc(firebase, COLLECTIONS.USERS, userId),
      (snapshot) => {
        const nextUserData = snapshot.exists()
          ? { id: snapshot.id, ...snapshot.data() }
          : null;

        setUserData(nextUserData);
        setCompanyId(
          nextUserData?.role === "companyUser"
            ? nextUserData.id
            : nextUserData?.companyId || null
        );
        setLoadingCollections(nextUserData?.role === "companyUser");
        setLoadingUser(false);
      },
      () => {
        setUserData(null);
        setCompanyId(null);
        setLoadingUser(false);
      }
    );

    return unsubscribe;
  }, [userId]);

  useEffect(() => {
    if (!userId || !companyId || userData?.role !== "companyUser") {
      setClientAdded(false);
      setCrewAdded(false);
      setShiftScheduled(false);
      setLoadingCollections(false);
      return undefined;
    }

    setLoadingCollections(true);
    const readiness = {
      clients: false,
      employees: false,
      shifts: false,
    };
    const markReady = (key) => {
      readiness[key] = true;
      if (readiness.clients && readiness.employees && readiness.shifts) {
        setLoadingCollections(false);
      }
    };

    const clientsUnsubscribe = onSnapshot(
      query(
        collection(firebase, COLLECTIONS.CLIENT),
        where("companyId", "==", companyId),
        limit(1)
      ),
      (snapshot) => {
        setClientAdded(!snapshot.empty);
        markReady("clients");
      },
      () => {
        setClientAdded(false);
        markReady("clients");
      }
    );

    const employeesUnsubscribe = onSnapshot(
      query(
        collection(firebase, COLLECTIONS.USERS),
        or(
          and(where("companyId", "==", companyId), where("role", "==", "Employee")),
          and(
            where("companyId", "==", companyId),
            where("role", "array-contains", "Employee")
          )
        ),
        limit(1)
      ),
      (snapshot) => {
        setCrewAdded(!snapshot.empty);
        markReady("employees");
      },
      () => {
        setCrewAdded(false);
        markReady("employees");
      }
    );

    const shiftsUnsubscribe = onSnapshot(
      query(
        collection(firebase, COLLECTIONS.APPOINMENTS),
        where("companyId", "==", companyId),
        limit(1)
      ),
      (snapshot) => {
        setShiftScheduled(!snapshot.empty);
        markReady("shifts");
      },
      () => {
        setShiftScheduled(false);
        markReady("shifts");
      }
    );

    return () => {
      clientsUnsubscribe();
      employeesUnsubscribe();
      shiftsUnsubscribe();
    };
  }, [companyId, userData?.role, userId]);

  const progress = useMemo(() => {
    const profileComplete = getCompanyProfileCompletion(userData);
    const buildingAdded = clientAdded;

    return {
      profileComplete,
      clientAdded,
      buildingAdded,
      crewAdded,
      shiftScheduled,
    };
  }, [clientAdded, crewAdded, shiftScheduled, userData]);

  const optionalProfileFieldsMissing = useMemo(
    () => getOptionalProfileFieldsMissing(userData),
    [userData]
  );

  const optionalProfileFieldLabels = useMemo(
    () =>
      Object.entries(optionalProfileFieldsMissing)
        .filter(([, isMissing]) => isMissing)
        .map(([fieldName]) => OPTIONAL_PROFILE_FIELD_LABELS[fieldName]),
    [optionalProfileFieldsMissing]
  );

  useEffect(() => {
    if (!userId || userData?.role !== "companyUser") {
      return undefined;
    }

    const storedProgress = userData?.onboardingProgress || EMPTY_PROGRESS;
    if (areProgressStatesEqual(storedProgress, progress)) {
      return undefined;
    }

    updateDoc(doc(firebase, COLLECTIONS.USERS, userId), {
      onboardingProgress: progress,
    }).catch((error) => {
      console.error("Failed to sync onboarding progress:", error);
    });

    return undefined;
  }, [progress, userData, userId]);

  const modalCompletedSteps = useMemo(
    () =>
      [
        progress.profileComplete,
        progress.clientAdded,
        progress.crewAdded,
        progress.shiftScheduled,
      ].filter(Boolean).length,
    [progress]
  );

  const completedSteps = modalCompletedSteps;
  const totalSteps = 4;
  const modalTotalSteps = 4;

  const modalProgressPercentage = useMemo(
    () =>
      modalTotalSteps
        ? Math.min((modalCompletedSteps / modalTotalSteps) * 100, 100)
        : 0,
    [modalCompletedSteps, modalTotalSteps]
  );

  return {
    loading: loadingUser || loadingCollections,
    progress,
    completedSteps,
    totalSteps,
    modalCompletedSteps,
    modalTotalSteps,
    modalProgressPercentage,
    isComplete: modalCompletedSteps === modalTotalSteps,
    isCompanyUser: userData?.role === "companyUser" && !hasEmployeeRole(userData?.role),
    userData,
    optionalProfileFieldsMissing,
    optionalProfileFieldLabels,
    hasOptionalProfileFieldsMissing: optionalProfileFieldLabels.length > 0,
  };
}
